if (!customElements.get('gn-pdp-story-v15')) {
  customElements.define('gn-pdp-story-v15', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.observers = [];
      this.timers = new Set();
      this.querySelectorAll('[data-story-carousel]').forEach(carousel => this.initCarousel(carousel, signal));
      this.querySelectorAll('[data-science]').forEach(science => {
        const panels = Array.from(science.querySelectorAll('[data-science-panel]'));
        const images = science.querySelectorAll('[data-science-image]');
        panels.forEach(panel => panel.addEventListener('toggle', () => {
          if (!panel.open) return;
          panels.forEach(other => { if (other !== panel) other.open = false; });
          images.forEach(image => { image.hidden = image.dataset.scienceImage !== panel.dataset.sciencePanel; });
        }, { signal }));
      });
      const timeline = this.querySelector('[data-story-timeline]');
      if (timeline) {
        const update = () => {
          this.frame = 0;
          const bounds = timeline.getBoundingClientRect();
          const threshold = window.innerHeight * .72;
          const progress = this.reducedMotion.matches ? 1 : Math.max(0, Math.min(1, (threshold - bounds.top) / Math.max(1, bounds.height - 40)));
          timeline.style.setProperty('--timeline-progress', String(progress));
          timeline.querySelectorAll('[data-timeline-item]').forEach(item => {
            item.classList.toggle('is-reached', this.reducedMotion.matches || item.getBoundingClientRect().top < threshold);
          });
        };
        const schedule = () => { if (!this.frame) this.frame = window.requestAnimationFrame(update); };
        window.addEventListener('scroll', schedule, { passive: true, signal });
        window.addEventListener('resize', schedule, { passive: true, signal });
        this.reducedMotion.addEventListener('change', schedule, { signal });
        update();
      }
    }

    initCarousel(carousel, signal) {
      const track = carousel.querySelector('[data-carousel-track]');
      const items = Array.from(carousel.querySelectorAll('[data-carousel-item]'));
      const controls = carousel.querySelector('[data-carousel-controls]');
      const previous = carousel.querySelector('[data-carousel-prev]');
      const next = carousel.querySelector('[data-carousel-next]');
      const status = carousel.querySelector('[data-carousel-status]');
      if (!track || !items.length || !controls || !previous || !next) return;
      const maxScroll = () => Math.max(0, track.scrollWidth - track.clientWidth);
      const nearestIndex = () => {
        const first = items[0].offsetLeft;
        let nearest = 0;
        let distance = Infinity;
        items.forEach((item, index) => {
          const difference = Math.abs(item.offsetLeft - first - track.scrollLeft);
          if (difference < distance) { distance = difference; nearest = index; }
        });
        return nearest;
      };
      const refresh = () => {
        const overflow = maxScroll() > 2;
        controls.hidden = !overflow;
        previous.disabled = track.scrollLeft <= 2;
        next.disabled = track.scrollLeft >= maxScroll() - 2;
        const firstVisible = nearestIndex() + 1;
        if (status) status.textContent = (carousel.dataset.positionLabel || '[current] / [total]').replace('[current]', String(firstVisible)).replace('[total]', String(items.length));
      };
      const move = direction => {
        const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        const distance = items[0].getBoundingClientRect().width + gap;
        track.scrollTo({ left: Math.max(0, Math.min(maxScroll(), track.scrollLeft + direction * distance)), behavior: this.reducedMotion.matches ? 'auto' : 'smooth' });
      };
      previous.addEventListener('click', () => move(-1), { signal });
      next.addEventListener('click', () => move(1), { signal });
      track.addEventListener('keydown', event => {
        if (event.target !== track || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        if (event.key === 'Home' || event.key === 'End') track.scrollTo({ left: event.key === 'Home' ? 0 : maxScroll(), behavior: this.reducedMotion.matches ? 'auto' : 'smooth' });
        else move(event.key === 'ArrowRight' ? 1 : -1);
      }, { signal });
      let timer;
      track.addEventListener('scroll', () => {
        previous.disabled = track.scrollLeft <= 2;
        next.disabled = track.scrollLeft >= maxScroll() - 2;
        clearTimeout(timer);
        this.timers.delete(timer);
        timer = setTimeout(() => { this.timers.delete(timer); refresh(); }, 120);
        this.timers.add(timer);
      }, { passive: true, signal });
      if ('ResizeObserver' in window) {
        const observer = new ResizeObserver(refresh);
        observer.observe(track);
        this.observers.push(observer);
      } else window.addEventListener('resize', refresh, { signal });
      refresh();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.observers?.forEach(observer => observer.disconnect());
      this.timers?.forEach(timer => clearTimeout(timer));
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = 0;
    }
  });
}
